import { ReactNode, useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import { getCreditDebitNote } from "../services";
import { CreditDebitNoteDetail } from "../types/credit-debit-note.type";
import { formatDateTimeBkk } from "../utils/date";
import { formatOrderDate } from "../components/SpecialOrder/data";
import { NoteStatusChip, NoteTypeChip } from "../components/CreditDebitNote/Chips";
import NoteEtaxStatus from "../components/CreditDebitNote/NoteEtaxStatus";
import NoteItemsTable from "../components/CreditDebitNote/NoteItemsTable";
import NoteTotalsSummary from "../components/CreditDebitNote/NoteTotalsSummary";
import CancelNoteDialog from "../components/CreditDebitNote/CancelNoteDialog";
import StockReturnPanel from "../components/CreditDebitNote/StockReturnPanel";
import { useNotePdf } from "../components/CreditDebitNote/useNotePdf";
import { createdNoteWarnings } from "../components/CreditDebitNote/createdNoteState";
import {
  CREDIT_DEBIT_NOTE_MENU_TITLE,
  CREDIT_DEBIT_NOTE_PATH,
  ETAX_REASON_LABEL,
  NOTE_REASON_LABEL,
  NOTE_REASON_TYPE_LABEL,
  NOTE_SETTLEMENT_LABEL,
  taxTypeLabel,
} from "../components/CreditDebitNote/constants";

const breadcrumbLinkStyle = { textDecoration: "none", color: "inherit" };

function Field({ label, value }: Readonly<{ label: string; value: ReactNode }>) {
  return (
    <Stack>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} component="div" whiteSpace="pre-wrap">
        {value || "-"}
      </Typography>
    </Stack>
  );
}

function Breadcrumb({ current }: Readonly<{ current: string }>) {
  return (
    <Stack direction="row" alignItems="center" mb={2}>
      <Link to={CREDIT_DEBIT_NOTE_PATH} style={breadcrumbLinkStyle}>
        <Typography variant="body1" fontWeight={500}>
          {CREDIT_DEBIT_NOTE_MENU_TITLE}
        </Typography>
      </Link>
      <Typography variant="body1" sx={{ mx: 1 }}>
        /
      </Typography>
      <Typography variant="body1" color="primary">
        {current}
      </Typography>
    </Stack>
  );
}

function NoteActions({
  note,
  onCancelled,
}: Readonly<{ note: CreditDebitNoteDetail; onCancelled: () => void }>) {
  const { downloading, download } = useNotePdf(note.uuid, note.note_no);
  const [cancelOpen, setCancelOpen] = useState<boolean>(false);

  const handleOpenCancel = () => setCancelOpen(true);
  const handleCloseCancel = () => setCancelOpen(false);
  const handleCancelled = () => {
    setCancelOpen(false);
    onCancelled();
  };

  return (
    <Paper>
      <Stack gap={1}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          loading={downloading}
          onClick={download}
        >
          ดาวน์โหลด PDF
        </Button>
        {note.status === "ISSUED" && (
          <Button variant="outlined" color="error" onClick={handleOpenCancel}>
            ยกเลิก
          </Button>
        )}
      </Stack>
      <CancelNoteDialog
        open={cancelOpen}
        note={note}
        onClose={handleCloseCancel}
        onCancelled={handleCancelled}
      />
    </Paper>
  );
}

/** The buyer snapshot taken when the note was issued, on one line. */
const buyerSummary = (note: CreditDebitNoteDetail): string =>
  [
    note.customer_code,
    note.buyer_tax_id && `เลขผู้เสียภาษี ${note.buyer_tax_id}`,
    note.buyer_branch && `สาขา ${note.buyer_branch}`,
    note.buyer_contact && `ผู้ติดต่อ ${note.buyer_contact}`,
    note.buyer_phone && `โทร ${note.buyer_phone}`,
  ]
    .filter(Boolean)
    .join(" · ");

/** When, by whom and why a cancelled note was cancelled. */
const cancelSummary = (note: CreditDebitNoteDetail): string => {
  const cancelledAt = formatDateTimeBkk(note.cancelled_at);
  return [
    "ยกเลิกแล้ว",
    cancelledAt && `เมื่อ ${cancelledAt}`,
    note.cancelled_by && `โดย ${note.cancelled_by}`,
    `· เหตุผล: ${note.cancel_reason ?? "-"}`,
  ]
    .filter(Boolean)
    .join(" ");
};

function NoteHeader({ note }: Readonly<{ note: CreditDebitNoteDetail }>) {
  const etaxReason = `${note.etax_reason_code} ${ETAX_REASON_LABEL[note.etax_reason_code]}`;

  return (
    <Paper sx={{ mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Stack>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h6" fontWeight={700}>
              {note.note_no}
            </Typography>
            <NoteTypeChip noteType={note.note_type} />
            <NoteStatusChip status={note.status} />
          </Stack>
          <Typography variant="body1" mt={0.5}>
            {note.buyer_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {buyerSummary(note)}
          </Typography>
          {note.buyer_address && (
            <Typography variant="caption" color="text.secondary">
              {note.buyer_address}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" gap={3} flexWrap="wrap" alignItems="flex-start">
          <Field label="วันที่เอกสาร" value={formatOrderDate(note.note_date)} />
          <Field label="เลขที่ใบกำกับภาษีเดิม" value={note.original_invoice_no} />
          <Field
            label="วันที่ใบกำกับภาษีเดิม"
            value={formatOrderDate(note.original_invoice_date)}
          />
          <Field label="สายส่ง" value={note.route_code} />
          <Field label="พนักงานขาย" value={note.emp_name} />
          <Field label="ราคาตามใบกำกับเดิม" value={taxTypeLabel(note.tax_type)} />
          <Field label="การชำระเงิน" value={NOTE_SETTLEMENT_LABEL[note.settlement_method]} />
          <Field label="ประเภทเหตุผล" value={NOTE_REASON_TYPE_LABEL[note.reason_type]} />
          <Field label="รหัสเหตุผล e-Tax" value={etaxReason} />
          <Field label="สถานะ e-Tax" value={<NoteEtaxStatus note={note} />} />
          <Field label="ผู้ออกเอกสาร" value={note.created_by} />
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function CreditDebitNoteDetailPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const location = useLocation();
  // What the create could not do (e.g. no goods back into stock), when the form opened this page.
  const createWarnings = createdNoteWarnings(location.state);
  const [note, setNote] = useState<CreditDebitNoteDetail | null>(null);
  const [notFound, setNotFound] = useState<boolean>(false);

  const fetchNote = useCallback(async () => {
    if (!uuid) return;
    try {
      const {
        data: { data },
      } = await getCreditDebitNote(uuid);
      setNote(data);
      setNotFound(!data);
    } catch {
      // The interceptor already shows why the note could not be read.
      setNotFound(true);
    }
  }, [uuid]);

  useEffect(() => {
    fetchNote();
  }, [fetchNote]);

  if (!note) {
    return (
      <Stack sx={{ padding: "8px 0" }}>
        <Breadcrumb current="…" />
        {notFound ? (
          <Typography color="text.secondary">ไม่พบเอกสารนี้</Typography>
        ) : (
          <Stack alignItems="center" py={8}>
            <CircularProgress />
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <Stack sx={{ padding: "8px 0" }}>
      <Breadcrumb current={note.note_no} />
      {createWarnings.map((warning) => (
        <Alert key={warning} severity="warning" sx={{ mb: 2 }}>
          {warning}
        </Alert>
      ))}
      {note.status === "CANCELLED" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {cancelSummary(note)}
        </Alert>
      )}
      <NoteHeader note={note} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 0, overflow: "hidden", mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} p={2}>
              รายการสินค้า ({note.items.length} รายการ)
            </Typography>
            <NoteItemsTable items={note.items} />
            <Stack direction="row" justifyContent="flex-end" p={2}>
              <Stack width={{ xs: "100%", sm: 360 }}>
                <NoteTotalsSummary
                  originalValue={note.original_value}
                  correctValue={note.correct_value}
                  difference={note.difference}
                  vatAmount={note.vat_amount}
                  grandTotal={note.grand_total}
                />
              </Stack>
            </Stack>
          </Paper>
          <Paper>
            <Stack gap={2}>
              <Field label={NOTE_REASON_LABEL[note.note_type]} value={note.reason} />
              <Field label="หมายเหตุ" value={note.remark} />
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack gap={2}>
            <NoteActions note={note} onCancelled={fetchNote} />
            <StockReturnPanel note={note} />
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}
